import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { CustomerParams, CustomerSortParameter, CustomerType } from 'store/customers/customer';
import { SortDirections } from 'utils/tableHelpers';

type CustomerParamsKey = keyof CustomerParams;

const defineCustomerParamKeys = <T extends Record<string, CustomerParamsKey>>(t: T) => t;

// This makes sure that the values match the parameters keys
export const ListCustomersParamsKeys = defineCustomerParamKeys({
    SORT_DIRECTION: 'sortDirection',
    SORT_PARAMERTER: 'sortParameter',
    PAGE: 'page',
    SIZE: 'size',
    TYPE: 'type',
    QUERY: 'query',
} as const);

type ListCustomersParamKey = typeof ListCustomersParamsKeys[keyof typeof ListCustomersParamsKeys];

const useCustomersFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get(ListCustomersParamsKeys.QUERY) || '';
    const initialSort =
        (searchParams.get(ListCustomersParamsKeys.SORT_PARAMERTER) as CustomerSortParameter) || undefined;
    const initialSortDir = (searchParams.get(ListCustomersParamsKeys.SORT_DIRECTION) as SortDirections) || undefined;
    const initialPage = Number(searchParams.get(ListCustomersParamsKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(ListCustomersParamsKeys.SIZE)) || 10;
    const initialCompanyType = (searchParams.get(ListCustomersParamsKeys.TYPE) as CustomerType) || undefined;

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);
    const [customerType, setCustomerType] = useState<CustomerType | undefined>(initialCompanyType);

    const [sortState, setSortState] = useState<{
        sort: CustomerSortParameter | undefined;
        sortDirection: SortDirections | undefined;
    }>({
        sort: initialSort,
        sortDirection: initialSortDir,
    });

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: Partial<CustomerParams>) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: ListCustomersParamKey, val?: string | null | undefined) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            apply(ListCustomersParamsKeys.QUERY, next.query);
            apply(ListCustomersParamsKeys.SORT_PARAMERTER, next.sortParameter);
            apply(ListCustomersParamsKeys.SORT_DIRECTION, next.sortDirection);
            apply(ListCustomersParamsKeys.PAGE, next.page?.toString());
            apply(ListCustomersParamsKeys.SIZE, next.size?.toString());
            apply(ListCustomersParamsKeys.TYPE, next.type);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            query: debouncedQuery,
            sortDirection: sortState.sortDirection,
            sortParameter: sortState.sort,
            page,
            size,
            type: customerType,
        });
    }, [customerType, debouncedQuery, page, size, sortState.sort, sortState.sortDirection, writeParams]);

    const appliedParams = useMemo(
        () => ({
            sortDirection: sortState.sortDirection,
            sort: sortState.sort,
            page,
            size,
            customerType,
            query: debouncedQuery || undefined,
        }),
        [customerType, debouncedQuery, page, size, sortState.sort, sortState.sortDirection]
    );

    const clearFilter = useCallback((key: ListCustomersParamKey) => {
        switch (key) {
            case ListCustomersParamsKeys.QUERY: {
                setQueryInputVal('');
                break;
            }

            case ListCustomersParamsKeys.PAGE: {
                setPage(0);
                break;
            }

            case ListCustomersParamsKeys.TYPE: {
                setCustomerType(undefined);
                break;
            }

            case ListCustomersParamsKeys.SORT_PARAMERTER: {
                setSortState({
                    sort: undefined,
                    sortDirection: undefined,
                });
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAllFilters = useCallback(() => {
        setPage(0);
        setQueryInputVal('');
        setCustomerType(undefined);
        setSortState({
            sort: undefined,
            sortDirection: undefined,
        });
    }, []);

    return {
        queryInputVal,
        appliedParams,
        clearFilter,
        clearAllFilters,
        setCustomerType,
        setQueryInputVal,
        setPage,
        setSize,
        setSortState,
    };
};

export default useCustomersFilters;
